import { useState } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/shared/components/ui/Tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/Dialog';
import { useDeleteIntegration } from '@/shared/hooks/useIntegrations';

interface DeleteIntegrationButtonProps {
  companyId: string;
  integrationId: string;
  provider: string;
}

export const DeleteIntegrationButton = ({
  companyId,
  integrationId,
  provider,
}: DeleteIntegrationButtonProps) => {
  const { t } = useTranslation('goals');
  const [showDialog, setShowDialog] = useState(false);
  const deleteIntegrationMutation = useDeleteIntegration();
  const isDeleting = deleteIntegrationMutation.isPending;

  const handleConfirm = async () => {
    try {
      await deleteIntegrationMutation.mutateAsync({
        companyId,
        integrationId,
      });
      toast.success(t('integrations.disconnectSuccess')(provider));
      setShowDialog(false);
    } catch {
      toast.error(t('integrations.disconnectFailed')(provider));
    }
  };

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setShowDialog(true)}
            disabled={isDeleting}
            className="text-muted-foreground hover:border-red-200 hover:bg-red-50 hover:text-red-600"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('integrationsDialog.disconnect')}</TooltipContent>
      </Tooltip>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>{t('integrationsDialog.disconnect')}</DialogTitle>
            <DialogDescription>
              {t('integrations.disconnectConfirm')(provider)}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDialog(false)}
              disabled={isDeleting}
            >
              {t('integrationsDialog.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirm}
              disabled={isDeleting}
            >
              {isDeleting
                ? t('integrationsDialog.disconnecting')
                : t('integrationsDialog.disconnect')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
